import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";

export const Route = createFileRoute("/gallery")({
  head: () => ({
    meta: [
      { title: "גלריה | ישיבת תורת חיים" },
      { name: "description", content: "גלריית תמונות של ישיבת תורת חיים" },
    ],
  }),
  component: GalleryPage,
});

function GalleryPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["gallery"],
    queryFn: () => api.gallery.list(),
  });

  const images: any[] = data?.images ?? [];
  const albums: string[] = data?.albums ?? [];
  const allAlbums = albums.length > 1 ? ["הכל", ...albums] : albums;
  const [activeAlbum, setActiveAlbum] = useState<string>("הכל");
  const [lightbox, setLightbox] = useState<{ url: string; caption: string | null } | null>(null);

  const filtered = activeAlbum === "הכל" ? images : images.filter((img) => img.album === activeAlbum);

  return (
    <>
      <section className="bg-primary text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="mx-auto mb-4 h-px w-16 bg-gold" />
          <h1 className="font-display text-5xl font-bold">גלריה</h1>
          <p className="mt-3 text-xl text-gold font-display">רגעים מחיי הישיבה</p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        {isLoading && (
          <div className="text-center text-muted-foreground py-20">טוען...</div>
        )}

        {!isLoading && images.length === 0 && (
          <div className="text-center text-muted-foreground py-20">אין תמונות בגלריה עדיין.</div>
        )}

        {!isLoading && allAlbums.length > 1 && (
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {allAlbums.map((album) => (
              <button
                key={album}
                onClick={() => setActiveAlbum(album)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors border ${
                  activeAlbum === album
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-foreground border-border hover:border-primary hover:text-primary"
                }`}
              >
                {album}
              </button>
            ))}
          </div>
        )}

        {!isLoading && activeAlbum !== "הכל" && (
          <h2 className="font-display text-2xl font-bold text-primary text-center mb-8">{activeAlbum}</h2>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((img: any) => (
            <button
              key={img.id}
              onClick={() => setLightbox({ url: img.url, caption: img.caption })}
              className="group relative overflow-hidden rounded-lg shadow-card border border-border/50 hover:shadow-elegant transition-shadow focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label={img.caption ?? "תמונה מהגלריה"}
            >
              <img
                src={img.url}
                alt={img.caption ?? "תמונה מהגלריה"}
                className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              {img.caption && (
                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                  {img.caption}
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label="תמונה מוגדלת"
        >
          <button
            className="absolute top-4 left-4 text-white text-3xl leading-none hover:text-gold"
            onClick={() => setLightbox(null)}
            aria-label="סגור"
          >
            ✕
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.url}
              alt={lightbox.caption ?? "תמונה מהגלריה"}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            {lightbox.caption && (
              <p className="mt-3 text-center text-white/80">{lightbox.caption}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
